import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    fetchPublicSignInfo,
    requestOtp,
    verifyOtp,
    fetchPublicDocument,
    submitSign,
    downloadSignedDocument,
    isPortalTimeoutError,
} from './MagicSignPortalService';
import type { SignGeo } from './MagicSignPortalService';
import { PublicSignInfo, PublicSignPortalStep } from './types';
import { OTP_COOLDOWN_SECONDS, OTP_MAX_ATTEMPTS, UNAVAILABLE_STATES, GEO_TIMEOUT_MS, PORTAL_TIMEOUT_MESSAGE } from './constants';

/** Logs the real error, then returns the timeout message when it's a timeout, else `fallback`. */
const logAndResolveMessage = (context: string, error: unknown, fallback: string): string => {
    console.error(`[MagicSignPortal] ${context}`, error);
    return isPortalTimeoutError(error) ? PORTAL_TIMEOUT_MESSAGE : fallback;
};

/**
 * Best-effort browser geolocation capture for the audit trail. Resolves with an empty object
 * (never rejects) if the API is unavailable, the user denies permission, or it times out —
 * geolocation is recorded when granted but never blocks signing.
 */
const captureGeolocation = (): Promise<SignGeo> =>
    new Promise((resolve) => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            resolve({});
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
            () => resolve({}),
            { timeout: GEO_TIMEOUT_MS, maximumAge: 0, enableHighAccuracy: true },
        );
    });

// OTP_MAX_ATTEMPTS is the maximum number of verify API calls allowed.
// The guard is `newAttempts > OTP_MAX_ATTEMPTS` so the Nth attempt where N === OTP_MAX_ATTEMPTS
// is still permitted — exactly OTP_MAX_ATTEMPTS calls reach the server before the block fires.

const useMagicSignPortalLogic = () => {
    const { token } = useParams<{ token: string }>();

    const [step, setStep] = useState<PublicSignPortalStep>('loading');
    const [info, setInfo] = useState<PublicSignInfo | null>(null);
    const [otp, setOtp] = useState('');
    const [otpError, setOtpError] = useState('');
    const [otpAttempts, setOtpAttempts] = useState(0);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const [documentUrl, setDocumentUrl] = useState('');
    const [documentLoading, setDocumentLoading] = useState(false);
    const [documentError, setDocumentError] = useState('');
    const [consentChecked, setConsentChecked] = useState(false);
    const [signing, setSigning] = useState(false);
    const [signError, setSignError] = useState('');
    const [infoError, setInfoError] = useState('');
    // True once this signatory has signed — drives the signed-copy view + Download Signed Copy CTA.
    const [signed, setSigned] = useState(false);

    const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const startCooldown = useCallback(() => {
        setCooldownSeconds(OTP_COOLDOWN_SECONDS);
        cooldownRef.current = setInterval(() => {
            setCooldownSeconds((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current!);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);

    useEffect(() => {
        return () => {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
        };
    }, []);

    // Revoke the blob URL when it changes or the component unmounts to prevent memory leaks.
    // Snapshot pattern: capture the url at effect-run time so the cleanup always
    // revokes the URL it observed, not the latest state value at cleanup time.
    useEffect(() => {
        const url = documentUrl;
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [documentUrl]);

    const loadInfo = useCallback(async () => {
        if (!token) {
            setStep('unavailable');
            return;
        }
        setStep('loading');
        try {
            const data = await fetchPublicSignInfo(token);
            setInfo(data);
            if (UNAVAILABLE_STATES.includes(data.status as (typeof UNAVAILABLE_STATES)[number])) {
                setStep('unavailable');
                return;
            }
            // Document-first (GST-magic-link model): open the document for review immediately;
            // OTP is requested only when the signatory clicks Sign/Accept.
            setStep('document');
            setDocumentLoading(true);
            setDocumentError('');
            try {
                const url = await fetchPublicDocument(token);
                setDocumentUrl(url);
            } catch (e) {
                console.error('[MagicSignPortal] Failed to load document', e);
                const msg = e instanceof Error && e.message ? e.message : 'Failed to load document. Please try again.';
                setDocumentError(msg);
            } finally {
                setDocumentLoading(false);
            }
        } catch (error) {
            setInfoError(
                logAndResolveMessage(
                    'Failed to load document info',
                    error,
                    'Failed to load document information. The link may be invalid or expired.',
                ),
            );
            setStep('unavailable');
        }
    }, [token]);

    useEffect(() => {
        loadInfo();
    }, [loadInfo]);

    const handleOtpChange = useCallback((value: string) => {
        setOtp(value);
        setOtpError('');
    }, []);

    const handleResendOtp = useCallback(async () => {
        if (!token || cooldownSeconds > 0) return;
        setOtpAttempts(0);
        setOtp('');
        setOtpError('');
        setSignError('');
        try {
            await requestOtp(token);
            startCooldown();
        } catch (error) {
            setOtpError(logAndResolveMessage('Failed to resend OTP', error, 'Failed to resend OTP. Please try again.'));
        }
    }, [token, cooldownSeconds, startCooldown]);

    // Clicking Sign/Accept on the document → request OTP and open the OTP modal
    const handleStartSign = useCallback(async () => {
        if (!token) return;
        setOtpAttempts(0);
        setOtp('');
        setOtpError('');
        setSignError('');
        try {
            await requestOtp(token);
            startCooldown();
        } catch (error) {
            // Surface in the modal; the user can still Resend
            setOtpError(logAndResolveMessage('Failed to send OTP', error, 'Failed to send OTP. Please use Resend.'));
        }
        setStep('otp');
    }, [token, startCooldown]);

    // In the OTP modal: verify the OTP, then apply the signature (verify + sign in one step)
    const handleVerifyOtp = useCallback(async () => {
        if (!token || otp.length < 6) return;
        const newAttempts = otpAttempts + 1;
        setOtpAttempts(newAttempts);

        if (newAttempts > OTP_MAX_ATTEMPTS) {
            setOtpError('Maximum attempts reached. Please request a new OTP.');
            return;
        }

        setSigning(true);
        setSignError('');
        setOtpError('');

        let authToken: string;
        try {
            const result = await verifyOtp(token, otp);
            authToken = result.authToken;
        } catch (error) {
            setOtpError(logAndResolveMessage('OTP verification failed', error, 'Invalid or expired OTP. Please try again.'));
            setOtp('');
            setSigning(false);
            return;
        }

        try {
            const geo = await captureGeolocation();
            await submitSign(token, authToken, true, geo);
            // Show the signed copy immediately: re-fetch the document with the signature stamped on.
            setSigned(true);
            try {
                const signedUrl = await fetchPublicDocument(token, '', true);
                setDocumentUrl(signedUrl);
            } catch (error) {
                // keep the existing document view if the signed re-fetch fails
                console.error('[MagicSignPortal] Failed to refresh signed document', error);
            }
            setStep('document');
        } catch (error) {
            setSignError(logAndResolveMessage('Failed to submit signature', error, 'Failed to apply signature. Please try again.'));
        } finally {
            setSigning(false);
        }
    }, [token, otp, otpAttempts]);

    // Closes the OTP modal and returns to document review
    const handleCancelOtp = useCallback(() => {
        setOtp('');
        setOtpError('');
        setStep('document');
    }, []);

    const handleDownloadSigned = useCallback(async () => {
        if (!token) return;
        try {
            await downloadSignedDocument(token);
        } catch (error) {
            // non-fatal
            console.error('[MagicSignPortal] Failed to download signed document', error);
        }
    }, [token]);

    const consentText =
        info?.consentTemplate?.replace('{document}', info?.documentName ?? 'this document') ??
        `I agree to use this e-Signature for signing ${info?.documentName ?? 'this document'}`;

    return {
        step,
        info,
        otp,
        otpError,
        cooldownSeconds,
        otpAttempts,
        documentUrl,
        documentLoading,
        documentError,
        consentChecked,
        setConsentChecked,
        signing,
        signError,
        infoError,
        signed,
        consentText,
        handleOtpChange,
        handleResendOtp,
        handleVerifyOtp,
        handleStartSign,
        handleCancelOtp,
        handleDownloadSigned,
    };
};

export default useMagicSignPortalLogic;
